import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import Script from 'next/script';
import { useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { gtm, scrollElement, selectElement } from 'tracking';

interface TrustedShopReviewCarouselProps {
  location: string;
}

const TRACKING_NAME = 'Trusted Shop Review Carousel';

/**
 * TrustedShopReviewCarousel component that displays reviews in a carousel format
 * and handles widget lifecycle properly
 */
const TrustedShopReviewCarousel: React.FC<TrustedShopReviewCarouselProps> = ({
  location,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const impressionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let unmounted = false;
    // Store ref value to use in cleanup
    const container = containerRef.current;

    // Re-initialize widget when component mounts
    const initWidget = (): void => {
      if (!unmounted && container) {
        const widget = document.createElement('etrusted-widget');
        widget.setAttribute(
          'data-etrusted-widget-id',
          'wdg-b9d1a500-14ed-4450-965d-6b71ba8046c1'
        );

        // Clean previous widget if exists
        const oldWidget = container.querySelector('etrusted-widget');
        if (oldWidget) {
          oldWidget.remove();
        }

        container.appendChild(widget);
      }
    };

    // Initialize after short delay to ensure proper setup
    const timer = setTimeout(initWidget, 100);

    return () => {
      unmounted = true;
      clearTimeout(timer);

      // Clean up widget on unmount using stored container reference
      if (container) {
        const widget = container.querySelector('etrusted-widget');
        if (widget) {
          widget.remove();
        }
      }
    };
  }, []);

  const swipeTracking = (): void => {
    gtm(
      scrollElement({
        element: [TRACKING_NAME, location],
      })
    );
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => swipeTracking(),
    onSwipedRight: () => swipeTracking(),
    swipeDuration: 500,
    preventScrollOnSwipe: true,
  });

  return (
    <>
      <Script
        src="https://integrations.etrusted.com/applications/widget.js/v2"
        strategy="lazyOnload"
      />
      <ImpressionTrackerNew element={[TRACKING_NAME, location]}>
        <div
          ref={impressionRef}
          onClick={() => {
            gtm(
              selectElement({
                element: [TRACKING_NAME, location],
              })
            );
          }}
        >
          <div {...handlers}>
            <div
              ref={containerRef}
              className="lg:px-4"
              data-test="trusted-shop-carousel-container"
            />
          </div>
        </div>
      </ImpressionTrackerNew>
    </>
  );
};

export default TrustedShopReviewCarousel;
